import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { getMainColor } from '@panel/commons/lib/theme';
import { selectColor, IconFill } from '@panel/commons';
import LeadCounterContainer from '../../../leadCounter';
import MessageCounter from '../../../messageActivityCounter';
import ItemBadget from '../../../itemBadget';
import { getParticularItems, getPublisherItems, getSeekerItems, mockUserType } from '../../../../../mockData';

const styledItemContainer = css`
  box-sizing: border-box;
  color: ${selectColor('gray100')};
  text-decoration: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding: 0px 8px;
  a {
    flex-wrap: wrap;
    color: green;
  }
  &::before {
    content: attr(title);
    content: attr(title) / '';
    height: 0;
    visibility: hidden;
    overflow: hidden;
    user-select: none;
    pointer-events: none;
    font-weight: 600;
  }
  :focus {
    outline: 0;
  }
  :hover {
    background-color: #fff;
    path {
      fill: #fc7b27;
    }
  }
  &:disabled {
    color: ${selectColor('gray25')};
    border: none;
    font-weight: 400;
    path {
      fill: ${IconFill('disabled')};
    }
  }
  &.active {
    background-color: ${selectColor('white')};
    border: none;
    span {
      color: ${selectColor('gray100')};
    }
    path {
      fill: ${IconFill('active')};
    }
  }
  margin: 0;
`;

const ItemContainer = styled.div`
  flex-grow: 1;
  display: flex;
`;

const LinkItem = styled(NavLink)`
  ${styledItemContainer}
`;

const AnchorItem = styled.a`
  ${styledItemContainer}
`;

const ItemName = styled.span`
  font-size: 16px;
  font-weight: 400;
`;

const ActiveLineColor = styled.span`
  height: 2px;
  width: 100%;
  margin-top: -17px;
  border-radius: 10px;
  background: transparent;
  ${LinkItem}:hover &, ${AnchorItem}:hover & {
    background: ${getMainColor()};
  }
  ${LinkItem}.active &, ${AnchorItem}.active & {
    background: ${getMainColor()};
  }
`;

const MenuItemContainer = styled.div``;

function HeaderMenuItemWrapper({ user, isLoading, site, isPortalWithNewReputation }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isInProfessionalPublisher = window.location.href.includes('publicador-profesionales');
  const publisherType = mockUserType.filter(index => index.id === user.publisherTypeId);
  const isBrasilSite = ['WIBR', 'IWBR', 'CMBR'].includes(site);
  const isAdminOrSuperuser = isPortalWithNewReputation
    ? ['1', '2', '3', '4', '20', '40'].includes(user.userTypeId)
    : ['1', '2'].includes(user.userTypeId);
  let items = [];
  if (user.publisherTypeId === '1' && !user.isSeeker) {
    items = getParticularItems('navbar');
  } else if (user.publisherTypeId !== '1' && user.isSeeker) {
    items = getSeekerItems('navbar');
  } else {
    items = getPublisherItems('navbar');
  }
  useEffect(() => {
    if (!pathname.includes('interesados')) {
      Cookies.set('changeInbox', true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  /**
   * La función evalua si el usuario tiene o no permisos para ver la pestaña
   * @param item - Recibe un item que representa una pestaña
   * @returns {boolean} - Retorna true si el usuario puede ver la pestaña enviada por param
   */
  const getIsAuthorizedTabByUserType = item => {
    if (!isAdminOrSuperuser && item.icon === 'stats') return false;
    return true;
  };

  return (
    <ItemContainer>
      {isLoading === false &&
        user.email &&
        items &&
        items.map(
          item =>
            ((item.icon === 'assessments' && user.showTasador) || item.icon !== 'assessments') &&
            (item.external ? (
              <MenuItemContainer key={item.icon}>
                <AnchorItem
                  className={
                    pathname === t(item.path) || (item.alternativePath && item.alternativePath.includes(pathname))
                      ? 'active'
                      : ''
                  }
                  href={t(item.path)}
                  rel='noopener noreferrer'
                  title={t(item.itemName)}
                  data-panel={`headerMenuItemDesk_${item.icon}`}
                >
                  <ItemName className='actionBtnName'>
                    {t(publisherType.length && item.icon === 'realEstate' ? publisherType[0].title : item.itemName)}
                  </ItemName>
                  <ActiveLineColor site={site} />
                </AnchorItem>
              </MenuItemContainer>
            ) : (
              <MenuItemContainer key={item.icon}>
                {getIsAuthorizedTabByUserType(item) && (
                  <>
                    {!isInProfessionalPublisher && (
                      <LinkItem
                        to={t(item.path)}
                        title={t(item.itemName)}
                        data-panel={`headerMenuItemDesk_${item.icon}`}
                        className={item.alternativePath && item.alternativePath.includes(pathname) ? 'active' : ''}
                      >
                        {item.icon === 'interested' && !user.isSeeker && user.publisherTypeId !== undefined && (
                          <LeadCounterContainer site={site} />
                        )}

                        {item.icon === 'activity' && <MessageCounter />}

                        {item.icon === 'stats' && isPortalWithNewReputation && isBrasilSite && (
                          <ItemBadget text={t('panel.reputacion.badge')} />
                        )}

                        <ItemName
                          className='actionBtnName'
                          data-tour-panel={item.itemName.includes('postings') ? 'header-postings' : null}
                        >
                          {t(
                            publisherType.length && item.icon === 'realEstate' ? publisherType[0].title : item.itemName
                          )}
                        </ItemName>
                        <ActiveLineColor site={site} />
                      </LinkItem>
                    )}
                  </>
                )}
              </MenuItemContainer>
            ))
        )}
    </ItemContainer>
  );
}

export { HeaderMenuItemWrapper };
